import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API_BASE } from "../../lib/api";
import { Upload, Loader2, CheckCircle2, AlertTriangle, FileText, RefreshCcw, RefreshCw, Clock, ArrowRight } from "lucide-react";

const POLL_MS = 2000;

const fmtPrice = (v) => (v == null ? "—" : "€ " + Number(v).toLocaleString("nl-BE"));
const fmtWhen = (iso) =>
  iso ? new Date(iso).toLocaleString("nl-BE", { dateStyle: "short", timeStyle: "short" }) : "—";

function AutoSyncPanel() {
  const [status, setStatus] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [note, setNote] = useState("");
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/admin/as24-sync/status`, { withCredentials: true });
      setStatus(data);
      const running = data?.latest?.status === "running";
      if (running && !pollRef.current) {
        pollRef.current = setInterval(load, 3000);
      } else if (!running && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch (e) {
      console.error("sync status", e);
    }
  };

  useEffect(() => {
    load();
    return () => pollRef.current && clearInterval(pollRef.current);
  }, []);

  const trigger = async () => {
    setTriggering(true);
    setNote("");
    try {
      const { data } = await axios.post(`${API_BASE}/admin/as24-sync/run`, {}, { withCredentials: true });
      if (data?.status === "already_running") {
        setNote("Er loopt al een sync — even geduld tot deze klaar is.");
      } else {
        setNote("Sync gestart… nieuwe wagens en prijzen worden opgehaald.");
      }
      // Poll a few times so the freshly-started run appears even if it kicks
      // off a little slowly (this also starts the live 3s poller via load()).
      [600, 1500, 3000, 6000, 10000].forEach((ms) => setTimeout(load, ms));
    } catch (e) {
      console.error("trigger", e);
      setNote("Sync starten mislukt. Probeer opnieuw.");
    } finally {
      setTriggering(false);
    }
  };

  const latest = status?.latest;
  const running = latest?.status === "running";

  return (
    <div className="bg-[#242424] border border-white/5 rounded-lg p-6 mb-8" data-testid="as24-autosync-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-[#c5a76f]" /> AutoScout24 sync
          </h2>
          <p className="text-white/50 text-xs mt-1 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> Automatische sync staat uit — druk op
            "Nu synchroniseren" na een aanpassing om nieuwe wagens toe te voegen en
            gewijzigde prijzen op te halen.
          </p>
        </div>
        <button
          onClick={trigger}
          disabled={triggering || running}
          data-testid="as24-sync-now-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#c5a76f] text-black text-xs font-semibold tracking-widest uppercase hover:bg-[#d8bd86] disabled:opacity-50 rounded transition-colors shrink-0"
        >
          {running || triggering ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {running ? "Bezig…" : "Nu synchroniseren"}
        </button>
      </div>

      {note && (
        <div className="mt-3 text-xs text-[#c5a76f]" data-testid="as24-sync-note">{note}</div>
      )}

      {latest && (
        <div className="mt-5 border-t border-white/8 pt-5" data-testid="as24-sync-latest">
          <div className="flex items-center gap-2 text-sm mb-3">
            {latest.status === "done" ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            ) : latest.status === "error" ? (
              <AlertTriangle className="w-4 h-4 text-red-400" />
            ) : (
              <Loader2 className="w-4 h-4 text-[#c5a76f] animate-spin" />
            )}
            <span className="text-white/80">
              {latest.status === "done"
                ? "Laatste sync voltooid"
                : latest.status === "error"
                ? "Laatste sync mislukt"
                : "Sync bezig…"}
            </span>
            <span className="text-white/40 text-xs">· {fmtWhen(latest.finished_at || latest.started_at)}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <Stat label="Op AutoScout" value={latest.total_on_as24 || 0} />
            <Stat label="Toegevoegd" value={latest.added || 0} accent />
            <Stat label="Prijs gewijzigd" value={latest.price_updated || 0} accent />
            <Stat label="Mislukt" value={latest.failed || 0} />
          </div>
          {(latest.added_titles || []).length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] tracking-widest uppercase text-white/40 mb-1.5">Nieuwe wagens</div>
              <ul className="space-y-1">
                {latest.added_titles.map((t, i) => (
                  <li key={i} className="text-xs text-white/70 flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-[#c5a76f]" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(latest.price_changes || []).length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] tracking-widest uppercase text-white/40 mb-1.5">Prijswijzigingen</div>
              <ul className="space-y-1">
                {latest.price_changes.map((p, i) => (
                  <li key={i} className="text-xs text-white/70 flex items-center gap-1.5 flex-wrap">
                    <span className="text-white/85">{p.title}</span>
                    <span className="text-white/40 line-through">{fmtPrice(p.old)}</span>
                    <ArrowRight className="w-3 h-3 text-[#c5a76f]" />
                    <span className="text-[#c5a76f] font-medium">{fmtPrice(p.new)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {latest.error && <div className="mt-3 text-xs text-red-300">{latest.error}</div>}
        </div>
      )}
    </div>
  );
}

export default function AdminImport() {
  const [file, setFile] = useState(null);
  const [wipeLegacy, setWipeLegacy] = useState(true);
  const [job, setJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const pollerRef = useRef(null);

  const stopPoll = () => {
    if (pollerRef.current) {
      clearInterval(pollerRef.current);
      pollerRef.current = null;
    }
  };

  const startPoll = (jobId) => {
    stopPoll();
    pollerRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/admin/import-jobs/${jobId}`, {
          withCredentials: true,
        });
        setJob(data);
        if (data.status === "done" || data.status === "failed") stopPoll();
      } catch (e) {
        console.error("poll", e);
      }
    }, POLL_MS);
  };

  useEffect(() => () => stopPoll(), []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!file) return setError("Selecteer eerst een CSV-bestand.");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("wipe_legacy", String(wipeLegacy));
      const { data } = await axios.post(`${API_BASE}/admin/import-autoscout24`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJob({ id: data.job_id, status: data.status });
      startPoll(data.job_id);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      setError(`Import mislukt: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    stopPoll();
    setJob(null);
    setFile(null);
    setError("");
  };

  const progress = job?.total
    ? Math.round(((job.imported + job.failed + job.gone) / job.total) * 100)
    : 0;

  return (
    <div className="px-8 py-10 max-w-[900px]" data-testid="admin-import-page">
      <h1 className="text-2xl font-bold mb-2">AutoScout24 import</h1>
      <p className="text-white/60 text-sm mb-8 leading-relaxed">
        Upload de KPI / "Listing health" CSV-export uit het AutoScout24 dealerportaal. Voor elke
        Listing GUID in het bestand wordt de volledige voertuigdetailpagina opgehaald, alle foto's
        gedownload + gecomprimeerd, en een wagen aangemaakt met automatische FR/EN vertaling.
      </p>

      <AutoSyncPanel />

      {!job && (
        <form onSubmit={onSubmit} className="space-y-6 bg-[#242424] border border-white/5 rounded-lg p-8">
          <div>
            <label className="block text-[#c5a76f] text-[11px] tracking-[0.25em] uppercase mb-3">
              CSV-bestand
            </label>
            <label
              htmlFor="as24-csv"
              className="flex items-center gap-3 px-5 py-4 bg-[#1a1a1a] border border-dashed border-white/15 hover:border-[#c5a76f]/40 rounded cursor-pointer text-white/70 transition-colors"
            >
              <FileText className="w-5 h-5 text-[#c5a76f]" />
              <span className="text-sm">
                {file ? file.name : "Kies een CSV-bestand (bv. 2142176321_2026_05_…csv)"}
              </span>
            </label>
            <input
              id="as24-csv"
              type="file"
              accept=".csv,text/csv"
              data-testid="import-csv-input"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>

          <label className="flex items-start gap-3 text-sm text-white/80">
            <input
              type="checkbox"
              checked={wipeLegacy}
              onChange={(e) => setWipeLegacy(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#c5a76f]"
              data-testid="import-wipe-legacy"
            />
            <span>
              <span className="font-medium">Wagens zonder AS24 link verwijderen</span> na succesvolle
              import.
              <span className="block text-white/40 text-xs mt-0.5">
                Aanbevolen. Verwijdert alleen wagens die niet via AS24 zijn binnengekomen
                (oude test data, handmatig toegevoegde wagens).
              </span>
            </span>
          </label>

          {error && (
            <div className="flex items-start gap-2 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded text-red-300 text-sm">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !file}
            data-testid="import-submit-btn"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#c5a76f] text-black text-sm font-semibold tracking-widest uppercase hover:bg-[#d8bd86] disabled:opacity-50 rounded transition-colors"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Start import
          </button>
        </form>
      )}

      {job && (
        <div className="bg-[#242424] border border-white/5 rounded-lg p-8 space-y-5" data-testid="import-job-status">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {job.status === "done" ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              ) : job.status === "failed" ? (
                <AlertTriangle className="w-5 h-5 text-red-400" />
              ) : (
                <Loader2 className="w-5 h-5 text-[#c5a76f] animate-spin" />
              )}
              <span className="font-semibold capitalize">
                {{
                  queued: "Wachtrij…",
                  running: "Import bezig…",
                  done: "Import voltooid",
                  failed: "Import mislukt",
                }[job.status] || job.status}
              </span>
            </div>
            <button
              onClick={reset}
              className="text-xs text-white/50 hover:text-[#c5a76f] inline-flex items-center gap-1"
            >
              <RefreshCcw className="w-3.5 h-3.5" /> Nieuwe import
            </button>
          </div>

          {job.total ? (
            <>
              <div className="h-2 bg-white/5 rounded overflow-hidden">
                <div
                  className="h-full bg-[#c5a76f] transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <Stat label="Totaal" value={job.total} />
                <Stat label="Geïmporteerd" value={job.imported || 0} accent />
                <Stat label="Mislukt" value={job.failed || 0} />
                <Stat label="Niet meer actief" value={job.gone || 0} />
              </div>
              {job.current_title && (
                <div className="text-xs text-white/50 truncate">
                  Huidig: <span className="text-white/80">{job.current_title}</span>
                </div>
              )}
              {job.status === "done" && (
                <div className="text-sm text-emerald-300/90">
                  ✓ {job.imported || 0} wagens geïmporteerd
                  {job.wiped_legacy > 0 && `, ${job.wiped_legacy} oude wagen(s) verwijderd`}.
                </div>
              )}
              {job.error && (
                <div className="text-sm text-red-300">{job.error}</div>
              )}
            </>
          ) : (
            <div className="text-white/40 text-sm">Initialiseren…</div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent = false }) {
  return (
    <div>
      <div className="text-[10px] tracking-widest uppercase text-white/40 mb-1">{label}</div>
      <div className={`text-2xl font-bold ${accent ? "text-[#c5a76f]" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}
